import { CurrencyCode } from "@smartrr/shared/currencyCode";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import {
  IPurchaseStateLineItem,
  IPurchaseStateLineItemPricingPolicy,
} from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ISmartrrSellingPlan, ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { DiscountValueType, IPurchaseStateLineItemDiscount } from "@smartrr/shared/interfaces/Discount";
import { convertNumberForFormatMoney } from "@smartrr/shared/utils/formatMoney";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";

import { defaultGetPrice, formatGetPrice, IFormatGetPriceOptions, IGetPrice } from "..";
import { getItemPrice } from "../Item";
import { MoneyHelper, SafeBigInt } from "../utils";
import { SellingPlanPricingPolicyAdjustmentType } from "@smartrr/shared/shopifyGraphQL/api";
import { produce } from "immer";
import partition from 'lodash/partition';

export const getPlan = (sellingPlanGroups: ISmartrrSellingPlanGroup[], planId: string | undefined) => {
  if (!planId) {
    return null;
  }
  for (const group of sellingPlanGroups) {
    for (const plan of group.sellingPlans) {
      if (plan.shopifyId === planId) {
        return plan;
      }
    }
  }
  return null;
};

const getRelevantPuchaseLineItems = (delivery: PurchaseStateAndDelivery, includeAddons: boolean) => {
  const cps = delivery.purchaseState;
  const scheduleIndex = Number.isInteger(delivery.indexFromScheduleStart) ? delivery.indexFromScheduleStart : 0;

  return cps.stLineItems.filter(lineItem => {
    const variant = lineItem.vnt;
    const validSellingPlan = Boolean(lineItem.sellingPlanId);
    const sameIndex = Number.isInteger(lineItem.skdIdx)
      ? includeAddons && lineItem.skdIdx === scheduleIndex
      : true;

    return sameIndex && variant.shopifyId && validSellingPlan;
  });
};

const calculatePreDiscountPrice = (
  deliverySellingPlanId: string | null,
  relevantPurchaseLines: IPurchaseStateLineItem[],
  sellingPlan: ISmartrrSellingPlan | null,
  sellingPlanGroups: ISmartrrSellingPlanGroup[] | null,
  orderNumber: number,
  currency: CurrencyCode,
  paymentMultipleDueOnDate: number,
  isPrepaid: boolean
) => {
  const lineItemPrices: Record<string, number> = {};

  const preDiscountAmount = relevantPurchaseLines.reduce((acc, lineItem) => {
    const plan = sellingPlan || (sellingPlanGroups ? getPlan(sellingPlanGroups, lineItem.sellingPlanId) : null);

    const lineItemPolicy: IPurchaseStateLineItemPricingPolicy = lineItem.pricingPolicy;

    const isSameSellingPlanAsDelivery =
      !!deliverySellingPlanId && deliverySellingPlanId === lineItem.sellingPlanId;

    const isPricingPolicyMissing = !lineItem.pricingPolicy && !!lineItem.basePrice;

    const needsPricingPolicyUpdate = lineItem.needsPricingUpdate ?? false;

    const generatedPolicyFromBasePrice: IPurchaseStateLineItemPricingPolicy | null =
      isSameSellingPlanAsDelivery && isPricingPolicyMissing
        ? {
            basePrice: {
              amount: convertNumberForFormatMoney(Number(lineItem.basePrice), lineItem.priceCurrency),
              currencyCode:
                lineItem.priceCurrency as IPurchaseStateLineItemPricingPolicy["basePrice"]["currencyCode"],
            },
            cycleDiscounts: [],
          }
        : null;

    const selectedPricingPolicy: IPurchaseStateLineItemPricingPolicy | null = needsPricingPolicyUpdate
      ? null
      : (lineItemPolicy ?? generatedPolicyFromBasePrice ?? null);

    const itemPrice = getItemPrice(lineItem.vnt, currency, {
      policy: selectedPricingPolicy,
      sellingPlan: plan,
      orderNumber,
    });

    lineItemPrices[lineItem.id] = itemPrice.current.price;

    const accInitial = SafeBigInt.fromDecimal(acc.initial.price);
    const accCurrent = SafeBigInt.fromDecimal(acc.current.price);
    const itemInitial = SafeBigInt.fromDecimal(itemPrice.initial.price);
    const itemCurrent = SafeBigInt.fromDecimal(itemPrice.current.price);

    let pricingQuantity = SafeBigInt.fromInteger(lineItem.quantity);

    if (
      isPrepaid &&
      selectedPricingPolicy?.cycleDiscounts[0].adjustmentType !== SellingPlanPricingPolicyAdjustmentType.Price
    ) {
      pricingQuantity =
        SafeBigInt.fromInteger(lineItem.quantity) * SafeBigInt.fromInteger(paymentMultipleDueOnDate);
    }

    const initialPrice = {
      price: MoneyHelper.fromBigInt(accInitial + itemInitial * pricingQuantity, "up"),
      priceCurrency: currency,
    };
    const currentPrice = {
      price: MoneyHelper.fromBigInt(accCurrent + itemCurrent * pricingQuantity, "up"),
      priceCurrency: currency,
    };
    return {
      initial: initialPrice,
      current: currentPrice,
    } as IGetPrice;
  }, defaultGetPrice(currency));

  return {
    amount: preDiscountAmount,
    individualPrices: lineItemPrices,
  };
};

const getPreDiscountPrice = (
  deliverySellingPlanId: string | null,
  relevantPurchaseLines: IPurchaseStateLineItem[],
  sellingPlanGroups: ISmartrrSellingPlanGroup[],
  orderNumber: number,
  currency: CurrencyCode,
  paymentMultipleDueOnDate: number,
  isPrepaid: boolean
) => {
  return calculatePreDiscountPrice(
    deliverySellingPlanId,
    relevantPurchaseLines,
    null,
    sellingPlanGroups,
    orderNumber,
    currency,
    paymentMultipleDueOnDate,
    isPrepaid
  );
};

export const getPreDiscountPriceWithSingleSellingPlan = (
  deliverySellingPlanId: string | null,
  relevantPurchaseLines: IPurchaseStateLineItem[],
  sellingPlan: ISmartrrSellingPlan,
  orderNumber: number,
  currency: CurrencyCode,
  paymentMultipleDueOnDate: number,
  isPrepaid: boolean
) => {
  return calculatePreDiscountPrice(
    deliverySellingPlanId,
    relevantPurchaseLines,
    sellingPlan,
    null,
    orderNumber,
    currency,
    paymentMultipleDueOnDate,
    isPrepaid
  );
};

export const usesRemaining = (discount: IPurchaseStateLineItemDiscount) => {
  if (discount.recurringCycleLimit === null || discount.recurringCycleLimit === undefined) {
    return Number.MAX_SAFE_INTEGER;
  }

  return discount.recurringCycleLimit - discount.usageCount;
};

export const getUpdatedDiscount = (
  updatedDiscounts: IPurchaseStateLineItemDiscount[],
  discountToGet: IPurchaseStateLineItemDiscount
) => {
  const vendorId = discountToGet.vendorId ?? "no-id";

  return updatedDiscounts.find(discount => {
    const searchVendorId = discount.vendorId ?? "no-id";

    return searchVendorId === vendorId;
  });
};

/**
 * Deduct discounts from the "current" delivery price. Filters out any discounts
 * that do not apply to the delivery or are not in the passed `updatedDiscounts`
 * param. Deducts line item discounts before applying any order-wide discounts.
 */
const subtractDiscounts = (
  lineItems: IPurchaseStateLineItem[],
  lineItemPrices: Record<string, number>,
  finalPrediscountPrice: IGetPrice,
  currency: CurrencyCode,
  unskippedDeliveryIndex: number,
  updatedDiscounts: IPurchaseStateLineItemDiscount[]
) => {
  const [orderWideDiscounts, lineItemDiscounts] = partition(
    validDiscountsForDeliveryIndex(lineItems, updatedDiscounts, unskippedDeliveryIndex),
    d => d.discount.orderWideDiscount
  );

  const lineItemDiscountTotal = lineItemDiscounts.reduce((acc, { lineItem, discount }) => {
    if (discount.valueType === DiscountValueType.FIXED) {
      const quantity = discount.quantityConsidered ? lineItem.quantity : 1;
      return acc + SafeBigInt.fromDecimal(convertNumberForFormatMoney(discount.value * quantity * 100, currency));
    }

    /** discount.valueType === DiscountValueType.PERCENTAGE **/
    const price = lineItemPrices[lineItem.id];
    return (
      acc +
      BigInt(lineItem.quantity) *
        ((SafeBigInt.fromDecimal(price) * SafeBigInt.fromDecimal(discount.value)) / SafeBigInt.fromDecimal(100))
    );
  }, SafeBigInt.fromDecimal(0));

  const priceAfterLineItemDiscounts = subtractDiscount(
    finalPrediscountPrice.current.price,
    lineItemDiscountTotal
  );

  const orderWideDiscountTotal = uniqueDiscounts(orderWideDiscounts).reduce((acc, { discount }) => {
    if (discount.valueType === DiscountValueType.FIXED) {
      return acc + SafeBigInt.fromDecimal(convertNumberForFormatMoney(discount.value * 100, currency));
    }

    /** discount.valueType === DiscountValueType.PERCENTAGE **/
    const remainingPrice = priceAfterLineItemDiscounts - acc;
    return acc + (remainingPrice * SafeBigInt.fromDecimal(discount.value)) / SafeBigInt.fromDecimal(100);
  }, SafeBigInt.fromDecimal(0));

  return produce(finalPrediscountPrice, draft => {
    draft.current.price = MoneyHelper.fromBigInt(
      subtractDiscount(finalPrediscountPrice.current.price, lineItemDiscountTotal + orderWideDiscountTotal),
      "up"
    );
  });
};

/**
 * Subtract the discount from the price and return the new price.
 * Handles BigInt conversions and precautionary normalization of the discount.
 */
function subtractDiscount(price: any, discount: bigint): bigint {
  const p = SafeBigInt.fromDecimal(price);
  return p - normalizedDiscount(discount, p);
}

/**
 * Ensure the discount is not less than zero or greater than the total delivery price.
 */
function normalizedDiscount(discountAmount: bigint, totalPrice: bigint): bigint {
  if (discountAmount < BigInt(0)) {
    return BigInt(0);
  }

  if (discountAmount > totalPrice) {
    return totalPrice;
  }

  return discountAmount;
}

/**
 * A discount together with the line item it corresponds to.
 */
interface ValidDiscount {
  lineItem: IPurchaseStateLineItem;
  discount: IPurchaseStateLineItemDiscount;
}

/**
 * Returns a unique array of discounts in terms of their vendor IDs.
 * In case of duplicates, keeps the first one found.
 */
function uniqueDiscounts(discounts: ValidDiscount[]): ValidDiscount[] {
  return discounts.reduce((unique, current) => {
    const vendorId = current.discount.vendorId ?? "no-id";
    const exists = unique.some(d => (d.discount.vendorId ?? "no-id") === vendorId);
    if (!exists) {
      unique.push(current);
    }
    return unique;
  }, [] as ValidDiscount[]);
}

/**
 * Collects discounts from the `lineItems` that are both present in the
 * `updatedDiscounts` and applicable to the provided `deliveryIndex`.
 */
function validDiscountsForDeliveryIndex(
  lineItems: IPurchaseStateLineItem[],
  updatedDiscounts: IPurchaseStateLineItemDiscount[],
  deliveryIndex: number
): ValidDiscount[] {
  return lineItems
    .flatMap(lineItem =>
      lineItem.discounts.map(discount => ({
        lineItem,
        discount: getUpdatedDiscount(updatedDiscounts, discount),
      }))
    )
    .filter((item): item is ValidDiscount => {
      return item.discount !== undefined && deliveryIndex < usesRemaining(item.discount);
    });
}

export const getDeliveryIndexExcludingSkipped = (delivery: PurchaseStateAndDelivery) => {
  if (delivery.indexFromNext === 0) {
    return 0;
  }

  let deliveryIndex = delivery.indexFromNext;

  const skips = delivery.purchaseState.schedule.skips;

  for (let skipIndex = 0; skipIndex < delivery.indexFromNext; skipIndex++) {
    const skippedSchedule = skips[skipIndex];

    if (skippedSchedule === undefined) {
      return deliveryIndex;
    }

    if (skippedSchedule === null) {
      continue;
    }

    if (skippedSchedule.hiddenSkipped || skippedSchedule.userSkipped) {
      deliveryIndex -= 1;
    }
  }

  return deliveryIndex;
};

interface DeliveryPriceConfig {
  includeAddons: boolean;
  pricingType: "subtotal" | "with_discounts" | "with_discounts_and_shipping";
}

export const getDeliveryPrice = (
  delivery: PurchaseStateAndDelivery,
  sellingPlanGroups: ISmartrrSellingPlanGroup[],
  config: DeliveryPriceConfig
): IGetPrice => {
  const cps = delivery.purchaseState;

  const relevantPurchaseLines = getRelevantPuchaseLineItems(delivery, config.includeAddons);
  const isPrepaid = isCPSPrepaid(cps.schedule);
  const { amount: finalPrediscountPrice, individualPrices: lineItemPrices } = getPreDiscountPrice(
    delivery.purchaseState.sellingPlanId ?? null,
    relevantPurchaseLines,
    sellingPlanGroups,
    (cps.schedule.totalOrdersCount ?? 1) + delivery.indexFromScheduleStart,
    cps.currency,
    delivery.paymentMultipleDueOnDate,
    isPrepaid
  );

  if (config.pricingType === "subtotal") {
    return finalPrediscountPrice;
  }

  const discountedPrice = subtractDiscounts(
    relevantPurchaseLines,
    lineItemPrices,
    finalPrediscountPrice,
    cps.currency,
    getDeliveryIndexExcludingSkipped(delivery),
    delivery.purchaseState.discounts
  );

  if (config.pricingType === "with_discounts") {
    return discountedPrice;
  }

  const shipping =
    isPrepaid && cps.schedule.orderCycleIndex !== 0
      ? cps.totalShipping * delivery.paymentMultipleDueOnDate
      : cps.totalShipping;

  const shippingAdded =
    SafeBigInt.fromDecimal(discountedPrice.current.price) +
    SafeBigInt.fromDecimal(shipping) -
    SafeBigInt.fromDecimal(cps.totalShippingDiscount ?? 0);

  return produce(discountedPrice, draft => {
    draft.current.price = MoneyHelper.fromBigInt(shippingAdded, "up");
  });
};

export const formatDeliveryPrice = (
  delivery: PurchaseStateAndDelivery,
  sellingPlanGroups: ISmartrrSellingPlanGroup[],
  config: DeliveryPriceConfig,
  options?: IFormatGetPriceOptions
) => {
  const cps = delivery.purchaseState;
  const isPrepaid = isCPSPrepaid(cps.schedule);

  if (isPrepaid && delivery.paymentMultipleDueOnDate === 0) {
    return typedLocalize("general.prepaid");
  }

  return formatGetPrice(getDeliveryPrice(delivery, sellingPlanGroups, config), cps.currency, options).current;
};
